import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, Subject, BehaviorSubject, of } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';

import { filter } from 'rxjs/operators';

import jwtDecode from 'jwt-decode';

import { environment } from '../../../environments/environment';
import { User } from '../models/user.model';
import { UserService } from './user.service';


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public lastUrl: string;

  private userLoggedIn = new Subject<boolean>();
  private user: any;
  private userDataTemp = new BehaviorSubject<any>(null);
  private avatarSub = new BehaviorSubject<string>("");
  
  constructor(
    private userService: UserService,
    private http: HttpClient, 
    private router: Router) {      
      this.userLoggedIn.next(false);
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.lastUrl = event.url;
      });
    }


  /*
   * Métdodo responsável por autenticar/enviar/receber as credenciais dos usuários
   */
  public login(email: string, password: string): Observable<User> {
    return this.http.post<User>(`${environment.api}/auths/authenticate`, { email, password })
    .pipe(
      take(1),
      map((result: any) => {
                
        if (result.data.accessToken && !result.data.blocked && result.data.accounts.length === 1) {
          this.setUserLoggedIn(true);
          this.setStorage(result.data);
          return result.data;
        }
                
        this.user = result.data;
        return this.user;
      }),
      catchError(this.handleError)      
    )
  }

  public verifyKey(key: string) {
    return this.http.post<any>(`${environment.api}/auths/validaccount`, { key }).pipe(
      map(result => {         
        this.userDataTemp.next(result.data);
        sessionStorage.removeItem('userDataTemp');
        sessionStorage.setItem(`userDataTemp`, btoa(JSON.stringify(result.data)));
        return result.data;
      }),
      catchError(this.handleError) 
    )
  }

  /*
   * Método de recuperação de senha
   */
  public forgotPassword(email: string): Observable<any> {    
    return this.http.patch<any>(`${environment.api}/auths/forgotpassword`, { email })
    .pipe(
      take(1),
      map(data => data),
      catchError(this.handleError)
    );
  }

  /*
   * Método responsável por verificar se o usuário estar logado
   */
  public isAuthenticated(): boolean {
        
    const key = sessionStorage.getItem('icbeu:d3d25d546e528d346231cd242ab2f1d1');
    const token = sessionStorage.getItem(key);

    // Valida se o token está salvo no sessionStorage               
    if (!token)
      return false;

    // Obtem os dados do sessionStorage do usuario
    const user: User = JSON.parse(atob(sessionStorage.getItem(`icbeu:user${key}`)));
    
         
    return true;    
  }

  /*
   * Método que retorna os dados do usuário logado
   */
  public getUser(): any {

    if (!this.user) {
      const key = sessionStorage.getItem('icbeu:d3d25d546e528d346231cd242ab2f1d1');
      const data = sessionStorage.getItem(`icbeu:user${key}`);

      if (!data)
        return null;

      return sessionStorage.getItem(`icbeu:user${key}`) ? JSON.parse(atob(sessionStorage.getItem(`icbeu:user${key}`))) : null;
    }

    return this.user;
  }

  public getUserTemp(): any {
    if (!this.userDataTemp.value) {      
      const user = sessionStorage.getItem('userDataTemp') ? JSON.parse(atob(sessionStorage.getItem('userDataTemp'))) : of(null);
      
      this.userDataTemp.next(user);
      return this.userDataTemp.asObservable();
    }
    
    return this.userDataTemp.asObservable();
  }

  public setAvatar(avatar: string, newImg: boolean = false) {
    if (newImg) {
      const key = sessionStorage.getItem('icbeu:d3d25d546e528d346231cd242ab2f1d1');    
      sessionStorage.setItem(`icbeu:useravatar${key}`, btoa(JSON.stringify(avatar)));    
    }
    
    this.avatarSub.next(avatar);
  }

  public getAvatar(): Observable<string> {    
    if (!this.avatarSub.value) {
      const key = sessionStorage.getItem('icbeu:d3d25d546e528d346231cd242ab2f1d1');
      const data = sessionStorage.getItem(`icbeu:useravatar${key}`);
      
      if (!data)
        return of(null);

      const avatar = sessionStorage.getItem(`icbeu:useravatar${key}`) ? JSON.parse(atob(sessionStorage.getItem(`icbeu:useravatar${key}`))) : of(null);
      this.avatarSub.next(avatar);
      return this.avatarSub.asObservable();
    }
    
    return this.avatarSub.asObservable();
  }

 
  setUserLoggedIn(userLoggedIn: boolean) {
    this.userLoggedIn.next(userLoggedIn);
  }

  getUserLoggedIn(): boolean {
    return this.isAuthenticated();    
  }

  /*
   * Método responsável por fazer os redirecionamentos via URL
   */
  public handleLogin(path: string = this.lastUrl): void {
    this.router.navigate(['/login', btoa(path)]);
  }

  /**
   * Decodifica o token
   */
  public decodeToken(): User {
    const key = sessionStorage.getItem('icbeu:d3d25d546e528d346231cd242ab2f1d1');
    const token = sessionStorage.getItem(key);

    // Valida se o token está salvo no sessionStorage               
    if (!token)
      return null;

    // Obtem os dados do sessionStorage do usuario
    const user: User = JSON.parse(atob(sessionStorage.getItem(`icbeu:user${key}`)));
    
    return jwtDecode(user.accessToken);
  }


  /*
   * Método responsável por deslogar o usuário
   */
  public logout(): void {
    this.user = null;
    this.userService.clearDataStudent();
    sessionStorage.clear();
    this.handleLogin();
  }

  /**
   * Description: Seta os dados do usuario no sessionStorage
   * @param user 
   */
  public setStorage(user: User) {    
    this.user = user;
        
    this.setAvatar(user.pathImg);

    sessionStorage.setItem(`icbeu:d3d25d546e528d346231cd242ab2f1d1`, user.key)
    sessionStorage.setItem(user.key, user.accessToken);
    sessionStorage.setItem(`icbeu:user${user.key}`, btoa(JSON.stringify(user)));
    sessionStorage.setItem(`icbeu:useravatar${user.key}`, btoa(JSON.stringify(user.pathImg)));
  }

  /**
   * Description: recebe o novo token
   * @param parameters 
   */
  public getNewToken(parameters: any): Observable<any> {   
    return this.http.post<any>(`${environment.api}/auths/newtoken`, parameters)    
    .pipe(
      take(1),
      map((result: any) => {
                
        if (result.data.accessToken && !result.data.blocked) {  

          this.setUserLoggedIn(true);
          this.setStorage(result.data);
          return result.data;
        }
        
        this.user = result.data;
        return this.user;
      }),
      catchError(this.handleError)
    );
  }

  public sendNotificationBlockTeacher(parameters: any): Observable<string> {    
    return this.http.post<any>(`${environment.api}/notifications`, parameters)    
    .pipe(
      take(1),
      map((result: any) => result.message),
      catchError(this.handleError)
    );
  }

  public checkEmailTypes(email: string): Observable<any[]> {
    return this.http.post(`${environment.api}/auths/check`, { email }).pipe(
      map((result: any) => result.data.accounts),
      catchError(this.handleError)
    )
  }
 
  // METHODS PRIVATES
  private handleError(error: any): Observable<any> {
    //console.error("ERRO NA REQUISIÇÃO =>", error);
    return throwError(error);
  }
  
}
